import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export interface TimeScale {
  readonly domainStartMs: number;
  readonly domainEndMs: number;
  readonly width: number;
  timeToX: (ms: number) => number;
  xToTime: (x: number) => number;
}

export const createTimeScale = ({
  domainStartMs,
  domainEndMs,
  width,
}: {
  domainStartMs: number;
  domainEndMs: number;
  width: number;
}): TimeScale => {
  const span = Math.max(domainEndMs - domainStartMs, 1);
  return {
    domainStartMs,
    domainEndMs,
    width,
    timeToX: (ms) => ((ms - domainStartMs) / span) * width,
    xToTime: (x) => domainStartMs + (x / width) * span,
  };
};

// Finest step whose tick count doesn't exceed `targetTicks` across the
// domain, kept to round wall-clock intervals so labels land on clean
// minute/hour/day boundaries.
const COARSEST_STEP_SECONDS = 24 * 3600;
const STEP_SECONDS = [
  10,
  30,
  60,
  5 * 60,
  15 * 60,
  30 * 60,
  60 * 60,
  3 * 3600,
  6 * 3600,
  12 * 3600,
  COARSEST_STEP_SECONDS,
] as const;

export interface TimeTick {
  readonly ms: number;
  readonly x: number;
}

export const timeAxisTicks = (
  scale: TimeScale,
  targetTicks: number
): TimeTick[] => {
  const spanSeconds = (scale.domainEndMs - scale.domainStartMs) / 1000;
  const stepSeconds =
    STEP_SECONDS.find((step) => spanSeconds / step <= targetTicks) ??
    COARSEST_STEP_SECONDS;
  const stepMs = stepSeconds * 1000;
  const firstMs = Math.ceil(scale.domainStartMs / stepMs) * stepMs;
  const ticks: TimeTick[] = [];
  for (let ms = firstMs; ms <= scale.domainEndMs; ms += stepMs) {
    ticks.push({ ms, x: scale.timeToX(ms) });
  }
  return ticks;
};

export const formatTick = (ms: number): string => {
  const at = dayjs.utc(ms);
  return at.hour() === 0 && at.minute() === 0
    ? at.format("MMM D")
    : at.format("HH:mm");
};
